import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.min.json";
import { countries as countryNames } from "@/modules/common/utils/countries";

/**
 * The country-code picker behind the workflow "tel" fields (client mobile on the Client Profile
 * step). Every territory libphonenumber knows EXCEPT the excluded ones — the same exclusion the
 * CRM's other phone inputs apply (`CountryPhoneInput` passes `excludedCountries: ["IL"]`).
 * Calling codes come from libphonenumber (the library crm-backend validates with), display
 * names from the CRM's country list; a territory that list lacks shows its ISO code.
 */
export interface PhoneCountry {
  iso: CountryCode;
  name: string;
  /** Digits only, no "+" — e.g. "20", "966", "1". */
  callingCode: string;
  flag: string;
}

const EXCLUDED_COUNTRIES: ReadonlySet<string> = new Set(["IL"]);

export const DEFAULT_PHONE_COUNTRY: CountryCode = "EG";

/** Regional-indicator pair for an ISO 3166-1 alpha-2 code — no image CDN, matches the rest of the step UI. */
const flagOf = (iso: string): string =>
  String.fromCodePoint(
    ...[...iso.toUpperCase()].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65)
  );

const NAME_BY_ISO = new Map<string, string>([
  ...countryNames.map((country): [string, string] => [country.countryCode, country.name]),
  // territories libphonenumber dials separately but the CRM country list does not carry
  ["AC", "Ascension Island"],
  ["BQ", "Caribbean Netherlands"],
  ["TA", "Tristan da Cunha"],
]);

export const PHONE_COUNTRIES: readonly PhoneCountry[] = getCountries()
  .filter((iso) => !EXCLUDED_COUNTRIES.has(iso))
  .map((iso) => ({
    iso,
    name: NAME_BY_ISO.get(iso) ?? iso,
    callingCode: getCountryCallingCode(iso),
    flag: flagOf(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const BY_ISO = new Map(PHONE_COUNTRIES.map((country) => [country.iso, country]));

export const phoneCountryByIso = (iso: string): PhoneCountry | undefined =>
  BY_ISO.get(iso as CountryCode);

// longest first so "+1264…" (Anguilla) is never read as "+1" + "264…" before the exact parse is tried
const CALLING_CODES = Object.keys(metadata.country_calling_codes).sort(
  (a, b) => b.length - a.length
);

/**
 * The picker entry a stored "+<code><local>" value belongs to, or undefined when the value has no
 * "+" prefix (legacy "01…" values), an unknown calling code, or an excluded country. A number that
 * parses resolves to its real territory (so "+1 416…" is Canada, not the US); a partial one falls
 * back to the calling code's main country (libphonenumber lists it first).
 */
export const phoneCountryOfValue = (value: string): PhoneCountry | undefined => {
  if (!value.startsWith("+")) return undefined;
  const parsedCountry = parsePhoneNumberFromString(value)?.country;
  const exact = parsedCountry ? BY_ISO.get(parsedCountry) : undefined;
  if (exact) return exact;
  const code = CALLING_CODES.find((candidate) =>
    value.startsWith(`+${candidate}`)
  );
  if (!code) return undefined;
  return metadata.country_calling_codes[code]
    .map((iso) => BY_ISO.get(iso))
    .find((country): country is PhoneCountry => country !== undefined);
};

/** Case-insensitive match on name, ISO code or calling code (with or without "+"). */
export const phoneCountryMatches = (country: PhoneCountry, query: string): boolean => {
  const needle = query.trim().toLowerCase().replace(/^\+/, "");
  if (!needle) return true;
  return (
    country.name.toLowerCase().includes(needle) ||
    country.iso.toLowerCase() === needle ||
    country.callingCode.startsWith(needle)
  );
};

const DEFAULT_COUNTRY: PhoneCountry =
  BY_ISO.get(DEFAULT_PHONE_COUNTRY) ?? PHONE_COUNTRIES[0];

/** A stored value split into its picker entry + local digits; an unrecognised value keeps the
 * default country and shows the whole raw string as the local part (legacy "01…" values). */
export const splitPhone = (value: string): { country: PhoneCountry; local: string } => {
  const country = phoneCountryOfValue(value);
  return country
    ? { country, local: value.slice(1 + country.callingCode.length) }
    : { country: DEFAULT_COUNTRY, local: value };
};

export const composePhone = (country: PhoneCountry, local: string): string => {
  const significant = local.trim().replace(/^0+/, "");
  return significant ? `+${country.callingCode}${significant}` : "";
};

/** The value any edit would store ("+20…", leading zeros stripped) — so callers can compare a
 * legacy unprefixed value against a touched one without a false "changed" (QA 2026-08-16: typing
 * the same digits back must not keep the phone-Save path alive). */
export const normalizePhone = (value: string): string => {
  const { country, local } = splitPhone(value);
  return composePhone(country, local);
};
